/**
 * Update Bank Account Modal
 * @description This component updates existing bank account
 * @author Tirumal Rao
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Button, Icon, Label, Message, Modal } from 'semantic-ui-react';
import { reduxForm, Field } from 'redux-form';
import {
  composeValidators,
  combineValidators,
  isRequired,
  isNumeric,
  isAlphaNumeric,
  hasLengthGreaterThan,
  hasLengthLessThan,
  matchesPattern
} from 'revalidate';
import { closeModal } from '../../modals/modalActions';
import TextInput from '../../../app/common/Form/TextInput';
import BanksDropdown from '../../SearchableDropDowns/BanksDropdown';
import { openModal } from '../../modals/modalActions';
import API from '../../../APIconfig';
import { accountUpdated, deleteOTP, generateOTP } from '../accountActions';
import { isMobile } from 'react-device-detect';

/**
 * ========================= CONFIGURATIONS START ===================
 */

const formName = 'updateBankAccount';

// Redux actions
const actions = {
  accountUpdated,
  closeModal,
  deleteOTP,
  generateOTP,
  openModal,
};

// Redux state
const mapState = state => ({
  auth: state.auth,
  async: state.async,
  bank: state.bank,
  initialValues: state.bank.select, // Prefill redux form values
  formState: state.form.updateBankAccount
});

/**
 * ========================= CONFIGURATIONS ENDS =====================
 */

/**
 * ========================== VALIDATIONS ============================
 */

const validate = combineValidators({
  // Account Number
  accountNumber: composeValidators(
    isRequired({ message: 'Account Number is required' }),
    isNumeric({ message: 'Account number should be numeric' }),
    hasLengthGreaterThan(8)({
      message: 'Account number length should be 9 digits to 18 digits long'
    }),
    hasLengthLessThan(19)({
      message: 'Account number length should be 9 digits to 18 digits long'
    })
  )(),

  // IFSC Code
  ifsc: composeValidators(
    isRequired({ message: 'IFSC code is required' }),
    isAlphaNumeric({ message: 'IFSC code should be alphanumeric' }),
    hasLengthGreaterThan(10)({
      message: 'IFSC code should be 11 characters long'
    }),
    hasLengthLessThan(12)({
      message: 'IFSC code should be 11 characters long'
    }),
    matchesPattern(/^[A-Za-z]{4}[0][A-Za-z0-9]{6}$/)({ message: 'Invalid IFSC Code' })
  )(),

  // Short name OR Account holder name
  shortName: composeValidators(
    isRequired({ message: 'This field is required' }),
    matchesPattern(/^[a-zA-Z ]+$/)({message: 'Account holder name should be alphabetic'}),
    hasLengthLessThan(86)({ message: 'Maximum length allowed is 85' })
  )(),

  // Bank name
  bankName: isRequired({ message: 'Bank name is required' })
});

/**
 * ========================== VALIDATIONS ENDS ======================
 */

class UpdateBankAccountModal extends Component {
  static propTypes = {
    accountUpdated: PropTypes.func.isRequired,
    async: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired,
    bank: PropTypes.object,
    closeModal: PropTypes.func.isRequired,
    deleteOTP: PropTypes.func.isRequired,
    formState: PropTypes.object,
    generateOTP: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired
  };

  // init state
  state = {
    ...this.props.account
  };

  updateBankAccount = values => {
    const {
      account: { accountToken },
      accountUpdated,
      auth,
      generateOTP,
      openModal
    } = this.props;

    // Generate OTP
    generateOTP(
      API.bank.generateOTPforupdateBank,
      { request: { accountDetails: { accountToken, ...values } } },
      auth.csrfRequestIdentifier,
      () => {
        // Construct payload
        const payload = {
          values:  { request: { accountDetails: { accountToken, ...values } } },
          csrfRequestIdentifier: auth.csrfRequestIdentifier,
          forwardAction: accountUpdated,
          api: {
            generate: API.bank.generateOTPforupdateBank,
            verify: API.bank.verifyOTPforupdateBank
          }
        };

        // Request to open OTP modal
        openModal('OTPModal', { payload });
      }
    );
  };

  onKeyPress = event => {
    if (event.key === 'Enter') {
      // This stops the form from being submitted
      event.preventDefault();
    }
  };

  render() {
    const {
      account,
      async,
      bank,
      closeModal,
      formState,
      handleSubmit,
      invalid,
      pristine
    } = this.props;
    return (
      <Modal closeIcon={true} size="tiny" open={true} onClose={closeModal}>
        <Modal.Header>Update Bank Account</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Form
              onSubmit={handleSubmit(this.updateBankAccount)}
              onKeyPress={this.onKeyPress}
              loading={async.loading && async.key === 'generateOTP'}
            >
              {formState &&
              formState.syncErrors &&
              formState.syncErrors.accountNumber && (
                <div>
                  <Label>
                    <Icon name="pencil" /> Please Re-enter your account number (
                    {account.accountNumber}) to update!
                  </Label>
                  <br />
                  <br />
                </div>
              )}

              <Field
                name="accountNumber"
                placeholder="Account Number"
                type={isMobile ? 'tel' : 'text'}
                maxLength={18}
                component={TextInput}
              />
              <BanksDropdown name="bankName" />
              <Field
                name="ifsc"
                placeholder="IFSC Code"
                type="text"
                maxLength={11}
                component={TextInput}
              />
              <Field
                name="shortName"
                placeholder="Account Holder Name"
                type="text"
                maxLength={85}
                component={TextInput}
              />
              {bank && bank.otp && bank.otp.error && bank.otp.error.errorMessage && (
                <Message negative>
                  <Message.Header>Error: {bank.otp.error.errorMessage}</Message.Header>
                </Message>
              )}
              <Button className="fcbtn fccancelbtn small" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                className="fcbtn fcprimary small"
                disabled={invalid || pristine}
                type="submit"
              >
                Save
              </Button>
            </Form>
          </Modal.Description>
        </Modal.Content>
      </Modal>
    );
  }

  componentDidMount() {
    const { deleteOTP } = this.props;

    deleteOTP();
  }
}

// Redux connect and reduxForm HOC on statefull component 'UpdateBankAccountModal'
export default connect(
  mapState,
  actions
)(
  reduxForm({
    form: formName,
    destroyOnUnmount: true,
    enableReinitialize: true,
    validate
  })(UpdateBankAccountModal)
);
